import * as THREE from 'three';
import { makeBulletMaterial, type BulletColors } from '../materials/bulletMaterial';

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _axisZ = new THREE.Vector3(0, 0, 1);

/**
 * Un lot de quads instanciés — une famille visuelle de projectiles, un seul
 * draw call quelle qu'en soit la quantité.
 *
 * C'est ce qui rend le compte de draw calls insensible au nombre de balles :
 * 2 000 projectiles répartis en quatre familles coûtent quatre draw calls, pas
 * deux mille. Le CPU de rendu n'est donc jamais le goulot d'un shmup — le
 * goulot est le fill rate (PLAN.md §1.1).
 *
 * Depuis le J3, l'apparence vient d'un shader à zones concentriques avec
 * anti-aliasing analytique, et non plus d'une texture. Aucun octet à charger,
 * un bord net à toute résolution, et une couleur exactement conforme à la
 * palette autorisée.
 */
export class QuadBatch {
  readonly mesh: THREE.InstancedMesh;
  private n = 0;
  /** Forme par instance (`aShape`) : sélectionne la silhouette dans le shader
   *  (orbe, faisceau, disque, missile). 0 = orbe historique. */
  private readonly shapeAttr: THREE.InstancedBufferAttribute;
  /** Teinte par instance (`aTint`) : multiplicateur de couleur. 1 = teinte de
   *  palette exacte. Porte le crépitement du faisceau en charge. */
  private readonly tintAttr: THREE.InstancedBufferAttribute;

  constructor(colors: BulletColors, capacity: number, additive: boolean, renderOrder: number) {
    const mat = makeBulletMaterial(colors, additive);

    /* ⚠️ Une géométrie PROPRE à ce lot, et non un quad partagé entre tous les
       lots : les attributs par-instance (`aShape`, `aTint`) vivent SUR la
       géométrie, or une géométrie partagée se ferait écraser d'un lot à l'autre.
       Quatre sommets : le coût d'une géométrie par lot est nul, et il n'y a
       qu'une poignée de lots (trois familles ennemies + trois lots joueur). */
    const geo = new THREE.PlaneGeometry(1, 1);
    // Défauts NEUTRES : forme 0 (orbe), teinte 1. Une instance jamais renseignée
    // rend donc EXACTEMENT comme avant — c'est ce qui garde les tirs joueur, qui
    // n'utilisent que l'orbe, au pixel près.
    this.shapeAttr = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    this.tintAttr = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
    this.shapeAttr.setUsage(THREE.DynamicDrawUsage);
    this.tintAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aShape', this.shapeAttr);
    geo.setAttribute('aTint', this.tintAttr);

    this.mesh = new THREE.InstancedMesh(geo, mat, capacity);
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.renderOrder = renderOrder;
    this.mesh.count = 0;
  }

  begin(): void {
    this.n = 0;
  }

  /** `angle` en radians ; le quad est allongé selon `lenScale` dans l'axe du
   *  mouvement, ce qui fait lire la vitesse dans la FORME avant de la lire dans
   *  la couleur.
   *
   *  `shape`/`tint` sont OPTIONNELS et rétro-compatibles : sans eux, l'instance
   *  garde la forme orbe et la teinte pleine, c'est-à-dire le comportement
   *  d'origine. Seules les balles ennemies exotiques (voir `BulletStyle`) les
   *  renseignent ; les tirs joueur appellent `add` à six arguments, inchangés. */
  add(
    x: number,
    y: number,
    z: number,
    size: number,
    lenScale: number,
    angle: number,
    shape = 0,
    tint = 1,
  ): void {
    if (this.n >= this.mesh.instanceMatrix.count) return;
    _p.set(x, y, z);
    _s.set(size, size * lenScale, 1);
    _q.setFromAxisAngle(_axisZ, angle);
    _m.compose(_p, _q, _s);
    this.mesh.setMatrixAt(this.n, _m);
    this.shapeAttr.setX(this.n, shape);
    this.tintAttr.setX(this.n, tint);
    this.n++;
  }

  end(): void {
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.shapeAttr.needsUpdate = true;
    this.tintAttr.needsUpdate = true;
  }
}
